import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, Timestamp, doc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import Header from './Header';
import DatePicker from 'react-datepicker'; // Thêm import
import 'react-datepicker/dist/react-datepicker.css'; // Thêm import CSS

// ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const KPIDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [kpiData, setKpiData] = useState({
    totalAssigned: 0,
    totalCompleted: 0,
    completedOnTime: 0,
    completedLate: 0,
    onTimeCompletionRate: 0,
    overdueTasks: 0,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [isTeamManager, setIsTeamManager] = useState(false);
  const [userListForDropdown, setUserListForDropdown] = useState([]);
  const [selectedUserIdForKPI, setSelectedUserIdForKPI] = useState(null); // UID of user to show KPIs for
  const [kpiSummary, setKpiSummary] = useState('');

  const adminEmails = ['ha.ttt@greenschool.edu.vn', 'maxdevfs@gmail.com'];

  // Hàm tính toán ngày hết hạn (tương tự như trong Dashboard.js hoặc TimeLine.js)
  const calculateDueDate = (startDate, duration) => {
    if (!startDate) return null;
    let start;
    if (startDate && typeof startDate.toDate === 'function') {
      start = startDate.toDate();
    } else if (startDate && startDate._seconds) {
      start = new Date(startDate._seconds * 1000);
    } else if (startDate instanceof Date) {
      start = startDate;
    } else {
      start = new Date(startDate);
    }
    if (isNaN(start.getTime())) return null;
    const dueDate = new Date(start);
    dueDate.setDate(dueDate.getDate() + (parseInt(duration) || 0));
    return dueDate;
  };

  useEffect(() => {
    const initDashboard = async () => {
      if (!auth.currentUser) {
        setLoading(false);
        // Reset states if no user is logged in
        setTasks([]);
        setKpiData({ totalAssigned: 0, totalCompleted: 0, completedOnTime: 0, completedLate: 0, onTimeCompletionRate: 0, overdueTasks: 0 });
        setIsSystemAdmin(false);
        setIsTeamManager(false);
        setUserListForDropdown([]);
        setSelectedUserIdForKPI(null);
        return;
      }

      setLoading(true);
      const currentUser = auth.currentUser;
      setSelectedUserIdForKPI(currentUser.uid); // Default to logged-in user's KPIs

      const systemAdminCheck = adminEmails.includes(currentUser.email);
      setIsSystemAdmin(systemAdminCheck);

      let usersForDropdown = [];
      let managerOfAnyTeam = false;

      if (systemAdminCheck) {
        // Fetch all users for system admin
        try {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          usersForDropdown = usersSnapshot.docs.map(doc => ({ uid: doc.id, displayName: doc.data().displayName || doc.data().email, email: doc.data().email }));
        } catch (error) {
          console.error("Error fetching users for admin:", error);
        }
      } else {
        // Check for team manager role and gather members from managed teams
        try {
          const teamsSnapshot = await getDocs(collection(db, 'teams'));
          const managedTeamMembers = new Map(); // To store unique members

          for (const teamDoc of teamsSnapshot.docs) {
            const teamData = teamDoc.data();
            if (teamData.membersData && Array.isArray(teamData.membersData)) {
              const isCurrentUserTeamAdminOrManager = teamData.membersData.some(
                member => member.uid === currentUser.uid && (member.role === 'admin' || member.role === 'manager')
              );
              if (isCurrentUserTeamAdminOrManager) {
                managerOfAnyTeam = true;
                teamData.membersData.forEach(member => {
                  if (!managedTeamMembers.has(member.uid)) {
                    managedTeamMembers.set(member.uid, { uid: member.uid, displayName: member.displayName || member.email, email: member.email });
                  }
                });
              }
            }
          }
          if (managerOfAnyTeam) {
            usersForDropdown = Array.from(managedTeamMembers.values());
          }
          setIsTeamManager(managerOfAnyTeam);
        } catch (error) {
          console.error("Error fetching teams/members for manager:", error);
        }
      }
      // Add current user to the top of the list if not already there, for easy access to own KPIs
      const currentUserInList = usersForDropdown.find(u => u.uid === currentUser.uid);
      if (!currentUserInList) {
        usersForDropdown.unshift({ uid: currentUser.uid, displayName: currentUser.displayName || currentUser.email, email: currentUser.email });
      }
      
      // If the user is a regular user (not system admin, not team manager), the list will just contain themselves
      if (!systemAdminCheck && !managerOfAnyTeam) {
         usersForDropdown = [{ uid: currentUser.uid, displayName: currentUser.displayName || currentUser.email, email: currentUser.email }];
      }
      setUserListForDropdown(usersForDropdown);
      setLoading(false); // Initial loading for roles and user list is done
    };

    initDashboard();
  }, [auth.currentUser]);


  useEffect(() => {
    const fetchTasksAndCalculateKPIs = async () => {
      // Use selectedUserIdForKPI to fetch tasks. If null (e.g. during initial load before user is set), use currentUser.uid
      const targetUserId = selectedUserIdForKPI || (auth.currentUser ? auth.currentUser.uid : null);

      if (!targetUserId) {
        setLoading(false);
        setTasks([]);
        setKpiData({ totalAssigned: 0, totalCompleted: 0, completedOnTime: 0, completedLate: 0, onTimeCompletionRate: 0, overdueTasks: 0 });
        return;
      }
      
      setLoading(true);
      try {
        const allUserTasks = [];
        const teamsSnapshot = await getDocs(collection(db, 'teams'));

        for (const teamDoc of teamsSnapshot.docs) {
          const teamData = teamDoc.data();
          if (teamData.tasks && Array.isArray(teamData.tasks)) {
            teamData.tasks.forEach(task => {
              if (task && task.assignees && Array.isArray(task.assignees)) {
                const isAssignedToTargetUser = task.assignees.some(
                  assignee => assignee && assignee.uid === targetUserId
                );
                if (isAssignedToTargetUser) {
                  allUserTasks.push({ ...task, id: task.id || `generated_${Math.random().toString(36).substr(2, 9)}`, teamId: teamDoc.id });
                }
              }
            });
          }
        }
        
        setTasks(allUserTasks);
        calculateAndSetKpis(allUserTasks, startDate, endDate);

      } catch (error) {
        console.error(`Error fetching tasks for KPI (user: ${targetUserId}):`, error);
        setTasks([]);
        setKpiData({ totalAssigned: 0, totalCompleted: 0, completedOnTime: 0, completedLate: 0, onTimeCompletionRate: 0, overdueTasks: 0 });
      } finally {
        setLoading(false);
      }
    };

    if (selectedUserIdForKPI) { // Only fetch if a user is selected
        fetchTasksAndCalculateKPIs();
    }
  }, [startDate, endDate, selectedUserIdForKPI, auth.currentUser]); // Re-run when selected user changes

  const calculateAndSetKpis = (currentTasks, currentStartDate, currentEndDate) => {
    let totalAssigned = 0;
    let totalCompleted = 0;
    let completedOnTime = 0;
    let completedLate = 0;
    let overdueTasks = 0;
    const today = new Date();
    // Đảm bảo currentStartDate và currentEndDate là đối tượng Date hoặc null
    const rangeStart = currentStartDate ? new Date(currentStartDate) : null;
    if (rangeStart) rangeStart.setHours(0, 0, 0, 0); // Set to start of day

    const rangeEnd = currentEndDate ? new Date(currentEndDate) : null;
    if (rangeEnd) rangeEnd.setHours(23, 59, 59, 999); // Set to end of day


    currentTasks.forEach(task => {
      const dueDate = calculateDueDate(task.start_date, task.duration);
      // Chuyển đổi Timestamp Firestore thành Date object một cách an toàn
      const createdAtTimestamp = task.createdAt;
      let createdAt = null;
      if (createdAtTimestamp && typeof createdAtTimestamp.toDate === 'function') {
        createdAt = createdAtTimestamp.toDate();
      } else if (createdAtTimestamp) {
        // Giả sử createdAtTimestamp là một string hoặc number có thể parse được
        const parsedDate = new Date(createdAtTimestamp);
        if (!isNaN(parsedDate.getTime())) { // Kiểm tra ngày hợp lệ
            createdAt = parsedDate;
        }
      }

      const completedAtTimestamp = task.completedAt;
      let completedAt = null;
      if (completedAtTimestamp && typeof completedAtTimestamp.toDate === 'function') {
        completedAt = completedAtTimestamp.toDate();
      } else if (completedAtTimestamp) {
        const parsedDate = new Date(completedAtTimestamp);
        if (!isNaN(parsedDate.getTime())) { // Kiểm tra ngày hợp lệ
            completedAt = parsedDate;
        }
      }

      // Lọc công việc được TẠO trong khoảng thời gian đã chọn
      // Bạn có thể thay đổi logic này để lọc theo ngày HOÀN THÀNH nếu muốn
      if (rangeStart && createdAt && createdAt < rangeStart) return;
      if (rangeEnd && createdAt && createdAt > rangeEnd) return;
      
      totalAssigned++;

      if (task.status === 'completed' && completedAt) {
        // Nếu muốn lọc công việc HOÀN THÀNH trong khoảng thời gian, thêm điều kiện ở đây:
        // if (rangeStart && completedAt < rangeStart) return;
        // if (rangeEnd && completedAt > rangeEnd) return;

        totalCompleted++;
        if (dueDate && completedAt <= dueDate) {
          completedOnTime++;
        } else if (dueDate && completedAt > dueDate) { // Chỉ tính là trễ hạn nếu có dueDate
          completedLate++;
        } else if (!dueDate) {
          // Nếu không có dueDate, vẫn tính là hoàn thành nhưng không thể xác định đúng/trễ hạn
          // Hoặc bạn có thể có logic khác ở đây
        }
      } else if (task.status !== 'completed' && dueDate && dueDate < today) {
        overdueTasks++;
      }
    });

    const onTimeCompletionRate = totalCompleted > 0 ? (completedOnTime / totalCompleted) * 100 : 0;

    setKpiData({
      totalAssigned,
      totalCompleted,
      completedOnTime,
      completedLate,
      onTimeCompletionRate: parseFloat(onTimeCompletionRate.toFixed(2)),
      overdueTasks,
    });

    // Generate KPI Summary
    generateKpiSummary(
      totalAssigned,
      totalCompleted,
      completedOnTime,
      completedLate,
      parseFloat(onTimeCompletionRate.toFixed(2)),
      overdueTasks,
      currentStartDate,
      currentEndDate
    );
  };

  const generateKpiSummary = (
    totalAssigned,
    totalCompleted,
    completedOnTime,
    completedLate,
    onTimeCompletionRate,
    overdueTasks,
    startDate,
    endDate
  ) => {
    if (totalAssigned === 0 && totalCompleted === 0 && overdueTasks === 0) {
      setKpiSummary('Hiện tại chưa có dữ liệu công việc nào trong khoảng thời gian này để đưa ra nhận xét. Hãy bắt đầu bằng cách thêm công việc mới nhé!');
      return;
    }

    let summary = '';
    const monthNames = ["tháng 1", "tháng 2", "tháng 3", "tháng 4", "tháng 5", "tháng 6", "tháng 7", "tháng 8", "tháng 9", "tháng 10", "tháng 11", "tháng 12"];
    let periodText = 'gần đây';
    let periodPrefix = 'Trong '; // Default prefix

    if (startDate && endDate) {
      const startDay = startDate.getDate();
      const startMonth = monthNames[startDate.getMonth()];
      const startYear = startDate.getFullYear();
      const endDay = endDate.getDate();
      const endMonth = monthNames[endDate.getMonth()];
      const endYear = endDate.getFullYear();

      if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
        periodText = `ngày ${startDay} ${startMonth} năm ${startYear}`;
      } else if (startYear === endYear && startMonth === endMonth) {
        periodText = `từ ngày ${startDay} đến ${endDay} ${startMonth} năm ${startYear}`;
      } else if (startYear === endYear) {
        periodText = `từ ${startDay} ${startMonth} đến ${endDay} ${endMonth} năm ${startYear}`;
      } else {
        periodText = `từ ${startDate.toLocaleDateString('vi-VN')} đến ${endDate.toLocaleDateString('vi-VN')}`;
      }
    } else if (startDate) {
        periodText = `từ ngày ${startDate.toLocaleDateString('vi-VN')}`;
    } else if (endDate) {
        periodText = `cho đến ngày ${endDate.toLocaleDateString('vi-VN')}`;
    } else {
        // If both startDate and endDate are null, periodText remains 'gần đây'
        periodPrefix = ''; // No prefix like "Trong" for "gần đây"
        periodText = 'Gần đây'; // Capitalize "Gần đây" when it starts the sentence
    }

    if (periodPrefix) {
        summary += `${periodPrefix}${periodText}, bạn đã được giao ${totalAssigned} công việc. `;
    } else {
        summary += `${periodText}, bạn đã được giao ${totalAssigned} công việc. `;
    }

    if (totalAssigned > 0) {
        summary += `Bạn đã hoàn thành ${totalCompleted} công việc. `;
    }


    if (totalCompleted > 0) {
      summary += `Tỷ lệ hoàn thành đúng hạn đạt ${onTimeCompletionRate}%. `;
      if (onTimeCompletionRate === 100) {
        summary += 'Một thành tích ấn tượng! Bạn đã hoàn thành tất cả công việc đúng hạn. Hãy tiếp tục phát huy tinh thần này nhé! ';
      } else if (onTimeCompletionRate >= 90) {
        summary += 'Kết quả rất tuyệt vời! Khả năng quản lý thời gian và hiệu suất của bạn đáng khen ngợi. ';
      } else if (onTimeCompletionRate >= 75) {
        summary += 'Làm tốt lắm! Phần lớn công việc đã được bạn xử lý đúng tiến độ. Cố gắng thêm chút nữa để đạt kết quả hoàn hảo nhé. ';
      } else if (onTimeCompletionRate >= 50) {
        summary += 'Bạn đã có cố gắng, tuy nhiên cần chú trọng hơn vào việc đảm bảo tiến độ. Hãy xem xét các yếu tố ảnh hưởng và cải thiện nhé. ';
      } else {
        summary += 'Tỷ lệ hoàn thành đúng hạn cần được cải thiện đáng kể. Đừng ngần ngại tìm kiếm sự hỗ trợ hoặc thay đổi cách tiếp cận để nâng cao hiệu quả. ';
      }
      summary += `Trong đó, có ${completedOnTime} công việc về đích đúng hạn và ${completedLate} công việc hoàn thành muộn hơn dự kiến. `;
      if (completedLate > 0) {
        summary += "Hãy rút kinh nghiệm từ những công việc này để lần sau tốt hơn. "
      }
    } else if (totalAssigned > 0) {
      summary += 'Hiện tại chưa có công việc nào được hoàn thành. Hãy tập trung và bắt đầu chinh phục các mục tiêu đã đặt ra! ';
    }

    if (overdueTasks > 0) {
      summary += `Có ${overdueTasks} công việc đang chờ bạn giải quyết và đã quá hạn. Đây là lúc để ưu tiên và xử lý chúng. Bạn có thể làm được! `;
    } else if (totalAssigned > 0 && totalCompleted < totalAssigned) {
      summary += 'Không có công việc nào bị quá hạn ở thời điểm này, đó là một tín hiệu tốt! Hãy tiếp tục hoàn thành các công việc còn lại. ';
    } else if (totalAssigned > 0 && totalCompleted === totalAssigned && overdueTasks === 0) {
      summary += 'Xuất sắc! Tất cả công việc đã được hoàn thành và không có gì quá hạn. Bạn đã làm rất tốt! ';
    } else if (totalAssigned === 0 && overdueTasks === 0 && totalCompleted === 0) {
        // This case is already handled at the beginning, but as a fallback:
        summary = 'Không có công việc nào được ghi nhận trong khoảng thời gian này. ';
    }


    // Adding some general encouragement
    if (totalAssigned > 0) {
        if (onTimeCompletionRate > 75) {
            summary += "Bạn đang làm rất tốt, hãy duy trì phong độ này! "
        } else if (totalCompleted > 0) {
            summary += "Mỗi một công việc hoàn thành là một bước tiến. Cố lên! "
        } else {
            summary += "Hãy bắt đầu với công việc đầu tiên, bạn sẽ thấy động lực hơn. "
        }
    }


    setKpiSummary(summary.trim());
  };

  // TODO: Thêm UI cho việc chọn dateRange (ví dụ: react-datepicker)
  // const handleDateChange = (newRange) => setDateRange(newRange);


  if (loading && tasks.length === 0) { // Refined loading condition
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  // Handler for dropdown change
  const handleUserSelectionChange = (event) => {
    setSelectedUserIdForKPI(event.target.value);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">KPI Dashboard</h1>
          {(isSystemAdmin || isTeamManager) && userListForDropdown.length > 0 && (
            <div className="mt-4 md:mt-0">
              <label htmlFor="user-kpi-select" className="sr-only">Xem KPI cho:</label>
              <select 
                id="user-kpi-select"
                value={selectedUserIdForKPI || ''}
                onChange={handleUserSelectionChange}
                className="block w-full md:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                {userListForDropdown.map(user => (
                  <option key={user.uid} value={user.uid}>
                    {user.displayName || user.email}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Chọn khoảng thời gian</h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Từ ngày</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="Chọn ngày bắt đầu"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white"
                isClearable
              />
            </div>
            <div className="flex-1">
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Đến ngày</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="Chọn ngày kết thúc"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white"
                isClearable
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <KpiCard title="Công việc được giao" value={kpiData.totalAssigned} icon="fas fa-tasks" />
          <KpiCard title="Công việc hoàn thành" value={kpiData.totalCompleted} icon="fas fa-check-circle" />
          <KpiCard title="Hoàn thành đúng hạn" value={kpiData.completedOnTime} icon="fas fa-calendar-check" />
          <KpiCard title="Hoàn thành trễ hạn" value={kpiData.completedLate} icon="fas fa-calendar-times" />
          <KpiCard title="Tỷ lệ đúng hạn" value={`${kpiData.onTimeCompletionRate}%`} icon="fas fa-percentage" />
          <KpiCard title="Công việc quá hạn" value={kpiData.overdueTasks} icon="fas fa-exclamation-circle" />
        </div>

        {/* KPI Summary Section */}
        {kpiSummary && (
          <div className="mt-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Nhận xét KPI</h2>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{kpiSummary}</p>
          </div>
        )}

        {/* TODO: Thêm biểu đồ nếu cần */}
        {/* <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Biểu đồ tiến độ</h2>
          <Bar data={...} options={...} />
        </div> */}
      </div>
    </div>
  );
};

const KpiCard = ({ title, value, icon }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex items-center space-x-4">
    <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
      <i className={`${icon} text-emerald-600 dark:text-emerald-400 text-xl`}></i>
    </div>
    <div>
      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
    </div>
  </div>
);

export default KPIDashboard;
